<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { RegisterTheme } from "@/common/themes";
import { iconMenu, svgIcon } from "@/common/components";

import MethodQuestionForm from "@/components/tabs/method-question.vue";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const accountId = computed(() => String(route.params.id));
const groupId = computed(() => String(route.params.group));

const menuTabs = computed(() => [
  {
    key: "edit",
    icon: "home-line",
    label: t("common.labels.group"),
  },
  {
    key: "field",
    icon: "field",
    label: t("common.labels.fields"),
  },
  {
    key: "method",
    icon: "method",
    label: t("common.labels.methods"),
  },
  {
    key: "question",
    icon: "form",
    label: t("common.labels.questions"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/method`);
}

function goToTab(tab: string) {
  router.push(`/${accountId.value}/register/method/${groupId.value}/${tab}`);
}
</script>

<template>
  <register-theme tab-active="method">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="method" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.method") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" active="question" @selected="goToTab($event)">
        <template #content>
          <method-question-form
            :account-id="accountId"
            :group-id="groupId"
            :remove="['PHOTO']"
          />
        </template>
      </icon-menu>
    </template>
  </register-theme>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}

.fields {
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;
  }

  .content {
    display: flex;
    flex-wrap: wrap;
    padding-block: 1rem;
    gap: 1rem;

    .item {
      user-select: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1rem;
      padding-block: 0.5rem;
      padding-inline: 0.75rem;
      border: 1px solid var(--border);
      border-radius: 0.5rem;

      &.active {
        border: 2px solid var(--primary);
      }

      .actions {
        user-select: none;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
      }

      button {
        cursor: pointer;
        user-select: none;
        font-size: 0.75rem;
        line-height: 0.75rem;
        background: #fff;
        outline: none;
        border: 1px solid var(--border);
        border-radius: 0.25rem;
        padding-block: 5px;
        padding-inline: 7px;

        &:hover {
          background: var(--gray);
        }
      }
    }
  }
}
</style>
